/**
 * Λεξοδάσος growth helpers.
 *
 * The forest is a per-tag collection of mini-trees — one species per
 * `error_tag` defined in [CLAUDE.md]. Each tree has its own growth counter
 * and stage, but ALL trees share the same 11-stage progression as the
 * main λεξοδέντρο (so a child can read the level at a glance).
 *
 * State shape:
 *   forest: { [tag]: { growth: number, stage: number } }
 *
 * Growth sources (each handled in `commitSession` / `commitMiniGame`):
 *
 *   1. Mastery (the big one): +10 to a tree when an item with that tag
 *      leaves the wrongPool. This is the primary teaching signal — the
 *      child is rewarded for actually fixing the mistakes the SM-2 system
 *      is flagging.
 *
 *   2. Drip: +1 to a tree on every first-attempt-correct answer for an
 *      item carrying that tag. Keeps trees alive when the child has no
 *      mistakes left — without this, a perfect-streak kid sees no forest
 *      progress at all.
 *
 *   3. Quest reward: +5 distributed evenly across all "active" trees on
 *      every completed daily quest. "Active" = trees that already have at
 *      least 1 growth point (i.e. the child has encountered the tag).
 *      Distribution rounds remainder up, so small quest counts are
 *      visible.
 *
 * Pure functions only — `commitSession` calls these to derive the next
 * forest snapshot before its single `set()` call.
 */

import { PET_STAGES } from '../data/petStages.js';
import { KNOWN_ERROR_TAGS } from './errorPattern.js';

const KNOWN_TAG_SET = new Set(KNOWN_ERROR_TAGS);

export const FOREST_REWARDS = {
  MASTERED_PER_TAG: 10,
  DRIP_PER_FIRST_CORRECT: 1,
  PER_QUEST_TOTAL: 5,
};

/**
 * Return a fresh, empty forest map. Keys are intentionally absent until the
 * child first encounters the tag — that way the UI can tell "never seen"
 * from "seen but at stage 0" if it wants to.
 */
export function initialForestState() {
  return {};
}

/**
 * Highest stage whose threshold is ≤ growth. Mirrors `petGrowth.stageForGrowth`
 * but kept separate so a future divergence (different curves for forest trees)
 * is a one-line change.
 */
export function stageForForestGrowth(growth) {
  let stage = PET_STAGES[0];
  for (const s of PET_STAGES) {
    if (growth >= s.threshold) stage = s;
    else break;
  }
  return stage;
}

/**
 * Add growth to a single tree. Returns the next snapshot for that tree.
 * `current` may be `undefined` — treated as a fresh tree at growth 0.
 */
export function bumpTree(current, delta) {
  const safe = current ?? { growth: 0, stage: 0 };
  const inc = Math.max(0, Math.floor(delta ?? 0));
  if (inc === 0) return safe;
  const nextGrowth = safe.growth + inc;
  const nextStage = stageForForestGrowth(nextGrowth).id;
  return {
    growth: nextGrowth,
    stage: Math.max(safe.stage ?? 0, nextStage),
  };
}

/**
 * Apply a per-tag growth delta map to the forest. Returns the next forest
 * snapshot + a list of trees whose stage advanced (used to surface a
 * celebration toast). Unknown tags are filtered out defensively so a bug
 * in upstream code can't pollute the forest with stray keys.
 */
export function applyTagDeltas(forest, deltas) {
  if (!deltas || typeof deltas !== 'object') {
    return { forest: forest ?? {}, stageChanges: [] };
  }
  const safe = forest ?? {};
  let mutated = false;
  const next = { ...safe };
  const stageChanges = [];

  for (const [tag, delta] of Object.entries(deltas)) {
    if (!KNOWN_TAG_SET.has(tag)) continue;
    const inc = Math.floor(delta ?? 0);
    if (!Number.isFinite(inc) || inc <= 0) continue;
    const before = safe[tag];
    const after = bumpTree(before, inc);
    if (after === before) continue;
    next[tag] = after;
    mutated = true;
    if (after.stage > (before?.stage ?? 0)) {
      stageChanges.push({ tag, fromStage: before?.stage ?? 0, toStage: after.stage });
    }
  }
  if (!mutated) {
    return { forest: safe, stageChanges: [] };
  }
  return { forest: next, stageChanges };
}

/**
 * Compute per-tag growth from the per-outcome trace assembled inside
 * `commitSession`. Pure — takes the same outcomes the store already loops
 * through, and the existing tracking record so it can tell mastery from
 * mere correctness.
 *
 * @param {Array} outcomes - same shape used by commitSession
 * @param {object} priorTracking - state.itemTracking before this commit
 * @param {object} nextTrackingMutations - the tracking patches computed by
 *        commitSession (so we can see who just left wrongPool)
 */
export function deltasFromOutcomes(outcomes, priorTracking, nextTrackingMutations) {
  const deltas = {};
  const safe = Array.isArray(outcomes) ? outcomes : [];
  const bump = (tag, n) => {
    if (!KNOWN_TAG_SET.has(tag) || n <= 0) return;
    deltas[tag] = (deltas[tag] ?? 0) + n;
  };

  for (const outcome of safe) {
    const item = outcome?.item;
    if (!item?.id) continue;
    const tags = Array.isArray(item.error_tags) ? item.error_tags : [];
    if (tags.length === 0) continue;

    // Drip on first-attempt-correct.
    if (outcome.firstAttemptCorrect === true) {
      for (const tag of tags) bump(tag, FOREST_REWARDS.DRIP_PER_FIRST_CORRECT);
    }

    // Mastery on wrongPool transition false → true (leaving the pool).
    const wasWrong = priorTracking?.[item.id]?.wrongPool === true;
    const nowCleared =
      nextTrackingMutations?.[item.id]?.wrongPool === false;
    if (wasWrong && nowCleared) {
      for (const tag of tags) bump(tag, FOREST_REWARDS.MASTERED_PER_TAG);
    }
  }
  return deltas;
}

/**
 * Distribute the quest-completion bonus across the child's "active" trees.
 *
 * Active = either the tag is already in the forest snapshot OR it appears in
 * the just-played outcomes (so a quest completed by the same session that
 * first introduced the tag still counts toward it). Tags with zero growth
 * AND no recent exposure are skipped — we don't want a brand-new account
 * to get all 11 trees bumped from a single Day-1 quest.
 *
 * Bonus is `PER_QUEST_TOTAL * questsCompleted`, integer-distributed evenly
 * (any remainder cycles through the active list in deterministic order).
 */
export function deltasFromQuests(forest, recentTags, questsCompleted) {
  const q = Math.max(0, Math.floor(questsCompleted ?? 0));
  if (q === 0) return {};
  const total = q * FOREST_REWARDS.PER_QUEST_TOTAL;
  if (total === 0) return {};

  const seenSet = new Set(Object.keys(forest ?? {}));
  for (const tag of recentTags ?? []) {
    if (KNOWN_TAG_SET.has(tag)) seenSet.add(tag);
  }
  const active = Array.from(seenSet).filter((t) => KNOWN_TAG_SET.has(t));
  if (active.length === 0) return {};

  // Sort for deterministic remainder distribution.
  active.sort();
  const per = Math.floor(total / active.length);
  let remainder = total - per * active.length;
  const deltas = {};
  for (const tag of active) {
    deltas[tag] = per + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder -= 1;
  }
  return deltas;
}

/**
 * Returns a UI-ready description of a single tree. Mirrors `describePet`.
 */
export function describeTree(tree) {
  const safe = tree ?? { growth: 0, stage: 0 };
  const current = stageForForestGrowth(safe.growth);
  const currentIdx = PET_STAGES.findIndex((s) => s.id === current.id);
  const next = PET_STAGES[currentIdx + 1] ?? null;
  if (!next) {
    return { current, next: null, progressToNext: 1, growthToNext: 0 };
  }
  const span = next.threshold - current.threshold;
  const progressed = safe.growth - current.threshold;
  return {
    current,
    next,
    progressToNext: Math.min(1, Math.max(0, progressed / span)),
    growthToNext: Math.max(0, next.threshold - safe.growth),
  };
}
